/**
 * Superadmin tenants management (ADR-0036 Phase 2). List / search / create /
 * suspend-reactivate / delete tenants, and open a detail drawer with live
 * physical counts. The control plane's main surface.
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  Building2,
  Loader2,
  Network,
  Pause,
  Play,
  Plus,
  Search,
  Trash2,
  TriangleAlert,
  Wrench,
} from "lucide-react";
import * as React from "react";
import { useNavigate } from "react-router-dom";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { EmptyState, PageContainer, PageContent, PageHeader } from "@/components/ui/page";
import { Table, Tbody, Td, Th, Thead, Tr } from "@/components/ui/table";
import {
  type ResolveResult,
  type Tenant,
  type TenantBillingRow,
  type TenantHealth,
  type TenantPlan,
  type TenantStatus,
  PlatformApiError,
  platformApi,
} from "@/lib/platform";

const TENANTS_KEY = ["platform", "tenants"];
const STATS_KEY = ["platform", "tenants", "stats"];
const OVERVIEW_KEY = ["platform", "tenants", "overview"];

/** A compact health pill for the tenants board (ADR-0036 diagnostics). */
function HealthBadge({ health }: { health: TenantHealth | undefined }) {
  if (!health) return <span className="text-xs text-muted-foreground">—</span>;
  // Split Control: the tenant lives on its cell — no local verdict, never fake drift.
  if (health.hosted_here === false)
    return (
      <Badge tone="neutral" title="Schema e migrazioni vivono sulla cella del tenant">
        Su cella
      </Badge>
    );
  if (health.ok) return <Badge tone="active">OK</Badge>;
  const reason = health.migration?.behind
    ? "Da migrare"
    : health.schema && !health.schema.complete
      ? "Schema incompleto"
      : "Da verificare";
  return (
    <Badge
      tone="warning"
      title={`migrazione: ${health.migration?.current ?? "—"} / ${health.migration?.head ?? "—"}`}
    >
      {reason}
    </Badge>
  );
}

const BILLING_KEY = ["platform", "tenants", "billing"];

/** Compact subscription pill for the tenants board (ADR-0041 §13). Tone + label derive
 * from the backend `state` (active / past_due / cancel_scheduled / canceled / none). */
function BillingBadge({ row }: { row: TenantBillingRow | undefined }) {
  if (!row || row.state === "none")
    return (
      <span className="text-xs text-muted-foreground" title="Nessun abbonamento (illimitato/comp)">
        —
      </span>
    );
  const renews = row.current_period_end
    ? `Si rinnova il ${new Date(row.current_period_end).toLocaleDateString()}`
    : undefined;
  if (row.state === "past_due")
    return <Badge tone="danger" title={row.past_due_at ? `In ritardo dal ${new Date(row.past_due_at).toLocaleDateString()}` : undefined}>In ritardo</Badge>;
  if (row.state === "cancel_scheduled")
    return <Badge tone="warning" title={renews && `In cancellazione · attivo fino al ${new Date(row.current_period_end as string).toLocaleDateString()}`}>In cancellazione</Badge>;
  if (row.state === "canceled") return <Badge tone="danger">Annullato</Badge>;
  // active → show the plan name (tone success); renewal date on hover.
  return (
    <Badge tone="active" title={renews}>
      {row.plan_name ?? "Attivo"}
      {row.billing_period === "year" ? " · anno" : ""}
    </Badge>
  );
}

/** Slugs the backend (`provisioning._RESERVED_SLUGS`) refuses — mirror them so the
 * dialog rejects up front instead of round-tripping a 409. */
const RESERVED_SLUGS = new Set([
  "public",
  "shared",
  "dbos",
  "sandbox",
  "pg",
  "information",
  // The `platform.<base>` control-plane host — a tenant must never shadow it.
  "platform",
]);

/**
 * The slug becomes a HOSTNAME (`<slug>.<base>`), so it is spelled with hyphens:
 * `noesis-srl.trayos.dev`. The underscore is not legal in a DNS label at all, which is
 * why it is refused rather than merely discouraged — and refusing it is also what keeps
 * the backend's slug→schema mapping (`-` → `_`) reversible.
 */
function slugify(raw: string): string {
  return raw
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .replace(/^[^a-z]+/, "") // a label must start with a letter
    .slice(0, 50)
    .replace(/-+$/, ""); // the slice may have left a trailing hyphen
}

/** Mirror of `provisioning.normalize_slug` — returns an error message or null. */
function slugError(slug: string): string | null {
  if (!slug) return "Inserisci uno slug.";
  if (slug.length > 50) return "Massimo 50 caratteri.";
  if (!/^[a-z][a-z0-9]*(-[a-z0-9]+)*$/.test(slug))
    return "Solo lettere minuscole, numeri e trattini (inizia con una lettera, niente underscore).";
  if (RESERVED_SLUGS.has(slug) || slug.startsWith("pg_") || slug.startsWith("sid-"))
    return `«${slug}» è riservato.`;
  return null;
}

function statusTone(status: TenantStatus): "active" | "warning" | "neutral" {
  if (status === "active") return "active";
  if (status === "suspended") return "warning";
  return "neutral";
}

function statusLabel(status: TenantStatus): string {
  return { active: "Attivo", suspended: "Sospeso", provisioning: "In creazione" }[status] ?? status;
}

export function PlatformTenantsPage() {
  const qc = useQueryClient();
  const navigate = useNavigate();
  const [creating, setCreating] = React.useState(false);
  const [query, setQuery] = React.useState("");
  const [deleteTarget, setDeleteTarget] = React.useState<Tenant | null>(null);

  const listQ = useQuery({ queryKey: TENANTS_KEY, queryFn: () => platformApi.listTenants() });
  const statsQ = useQuery({ queryKey: STATS_KEY, queryFn: () => platformApi.stats() });
  const overviewQ = useQuery({
    queryKey: OVERVIEW_KEY,
    queryFn: () => platformApi.tenantsOverview(),
  });
  const healthById = React.useMemo(
    () => new Map((overviewQ.data ?? []).map((h) => [h.tenant_id, h])),
    [overviewQ.data],
  );
  const billingQ = useQuery({
    queryKey: BILLING_KEY,
    queryFn: () => platformApi.tenantsBillingOverview(),
  });
  const billingById = React.useMemo(
    () => new Map((billingQ.data ?? []).map((b) => [b.tenant_id, b])),
    [billingQ.data],
  );
  // Remote tenants (split Control, ok=null) carry no local verdict — never count them.
  const anyBehind = (overviewQ.data ?? []).some((h) => h.hosted_here !== false && h.ok === false);

  const invalidate = () => {
    qc.invalidateQueries({ queryKey: TENANTS_KEY });
    qc.invalidateQueries({ queryKey: STATS_KEY });
    qc.invalidateQueries({ queryKey: OVERVIEW_KEY });
  };

  const migrateAll = useMutation({
    mutationFn: () => platformApi.migrateAllTenants(),
    onSettled: invalidate,
  });

  const setStatus = useMutation({
    mutationFn: ({ id, status }: { id: string; status: "active" | "suspended" }) =>
      platformApi.setTenantStatus(id, status),
    onSuccess: invalidate,
  });
  const remove = useMutation({
    mutationFn: (id: string) => platformApi.deleteTenant(id),
    onSuccess: () => {
      invalidate();
      setDeleteTarget(null);
    },
  });

  const all = listQ.data ?? [];
  const q = query.trim().toLowerCase();
  const rows = q
    ? all.filter(
        (t) =>
          t.slug.includes(q) ||
          t.schema_name.includes(q) ||
          (t.name ?? "").toLowerCase().includes(q),
      )
    : all;

  const stats = statsQ.data;

  return (
    <PageContainer>
      <PageHeader
        title="Tenant"
        description="Organizzazioni isolate della piattaforma. Ogni tenant è uno schema dedicato."
        actions={
          <div className="flex items-center gap-2">
            <Button
              size="sm"
              variant={anyBehind ? "default" : "outline"}
              disabled={migrateAll.isPending}
              title="Esegui le migrazioni in sospeso su tutti i tenant"
              onClick={() => migrateAll.mutate()}
            >
              {migrateAll.isPending ? (
                <Loader2 className="h-4 w-4 animate-spin" strokeWidth={1.5} />
              ) : (
                <Wrench className="h-4 w-4" strokeWidth={1.5} />
              )}
              Migra tutti
            </Button>
            <Button size="sm" onClick={() => setCreating(true)}>
              <Plus className="h-4 w-4" strokeWidth={1.5} />
              Nuovo tenant
            </Button>
          </div>
        }
      />
      <PageContent className="space-y-4">
        <div className="grid grid-cols-2 gap-3 sm:max-w-md">
          <StatCard label="Totale" value={stats?.total} />
          <StatCard label="Attivi" value={stats?.active} />
        </div>

        {migrateAll.data && (
          <div
            className={
              "rounded-md border p-3 text-sm " +
              (migrateAll.data.ok
                ? "border-success/30 bg-success/5 text-foreground"
                : "border-danger/30 bg-danger/5 text-danger")
            }
          >
            {migrateAll.data.ok
              ? "Migrazioni applicate a tutti i tenant."
              : `Errore migrazione: ${migrateAll.data.error}`}
          </div>
        )}

        <div className="relative max-w-sm">
          <Search className="pointer-events-none absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" strokeWidth={1.5} />
          <Input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Cerca per nome, slug o schema…"
            className="pl-8"
            aria-label="Cerca tenant"
          />
        </div>

        <ResolverDebug />

        {listQ.isError ? (
          <EmptyState
            icon={TriangleAlert}
            title="Impossibile caricare i tenant"
            description={
              listQ.error instanceof PlatformApiError ? listQ.error.message : "Errore di rete."
            }
            action={
              <Button variant="ghost" onClick={() => listQ.refetch()}>
                Riprova
              </Button>
            }
          />
        ) : rows.length === 0 && !listQ.isLoading ? (
          <EmptyState
            icon={Building2}
            title={q ? "Nessun risultato" : "Nessun tenant"}
            description={
              q
                ? "Nessun tenant corrisponde alla ricerca."
                : "Il tenant predefinito (public) è gestito dalle migrazioni. Crea il primo tenant aggiuntivo."
            }
            action={
              !q && (
                <Button onClick={() => setCreating(true)}>
                  <Plus className="h-4 w-4" strokeWidth={1.5} />
                  Nuovo tenant
                </Button>
              )
            }
          />
        ) : (
          <div className="overflow-hidden rounded-lg border border-border/60">
            <Table>
              <Thead>
                <Tr>
                  <Th>Nome</Th>
                  <Th>Slug</Th>
                  <Th>Schema</Th>
                  <Th>Stato</Th>
                  <Th>Abbonamento</Th>
                  <Th>Salute</Th>
                  <Th>Creato</Th>
                  <Th className="w-28 text-right">Azioni</Th>
                </Tr>
              </Thead>
              <Tbody>
                {rows.map((row) => {
                  const isPublic = row.schema_name === "public";
                  return (
                    <Tr
                      key={row.id}
                      className="cursor-pointer"
                      onClick={() => navigate(row.id)}
                    >
                      <Td className="font-medium">{row.name || row.slug}</Td>
                      <Td className="text-xs text-muted-foreground">{row.slug}</Td>
                      <Td className="font-mono text-xs text-muted-foreground">{row.schema_name}</Td>
                      <Td>
                        <Badge tone={statusTone(row.status)}>{statusLabel(row.status)}</Badge>
                      </Td>
                      <Td>
                        <BillingBadge row={billingById.get(row.id)} />
                      </Td>
                      <Td>
                        <HealthBadge health={healthById.get(row.id)} />
                      </Td>
                      <Td className="text-xs text-muted-foreground">
                        {new Date(row.created_at).toLocaleDateString()}
                      </Td>
                      <Td className="text-right" onClick={(e) => e.stopPropagation()}>
                        {!isPublic && (
                          <div className="flex items-center justify-end gap-1">
                            {row.status === "active" ? (
                              <Button
                                size="sm"
                                variant="ghost"
                                disabled={setStatus.isPending}
                                title="Sospendi tenant"
                                onClick={() => setStatus.mutate({ id: row.id, status: "suspended" })}
                              >
                                <Pause className="h-4 w-4 text-warning" strokeWidth={1.5} />
                              </Button>
                            ) : (
                              <Button
                                size="sm"
                                variant="ghost"
                                disabled={setStatus.isPending}
                                title="Riattiva tenant"
                                onClick={() => setStatus.mutate({ id: row.id, status: "active" })}
                              >
                                <Play className="h-4 w-4 text-success" strokeWidth={1.5} />
                              </Button>
                            )}
                            <Button
                              size="sm"
                              variant="ghost"
                              title="Elimina tenant"
                              onClick={() => setDeleteTarget(row)}
                            >
                              <Trash2 className="h-4 w-4 text-danger" strokeWidth={1.5} />
                            </Button>
                          </div>
                        )}
                      </Td>
                    </Tr>
                  );
                })}
              </Tbody>
            </Table>
          </div>
        )}
      </PageContent>

      {creating && <CreateTenantDialog onClose={() => setCreating(false)} onCreated={invalidate} />}
      {deleteTarget && (
        <DeleteTenantDialog
          tenant={deleteTarget}
          pending={remove.isPending}
          error={
            remove.isError
              ? remove.error instanceof PlatformApiError
                ? remove.error.message
                : "Eliminazione non riuscita"
              : null
          }
          onConfirm={() => remove.mutate(deleteTarget.id)}
          onClose={() => {
            remove.reset();
            setDeleteTarget(null);
          }}
        />
      )}
    </PageContainer>
  );
}

/** Routing debugger (T4) — given a Host, show what `resolver.classify_host` decides.
 * Collapsed by default; debugs "why does acme.tray.localhost land on public?". */
function ResolverDebug() {
  const [open, setOpen] = React.useState(false);
  const [host, setHost] = React.useState("");
  const [result, setResult] = React.useState<ResolveResult | null>(null);
  const probe = useMutation({
    mutationFn: (h: string) => platformApi.resolveHost(h),
    onSuccess: (r) => setResult(r),
  });

  const toneFor = (c: string) =>
    c === "tenant" || c === "default" || c === "platform" ? "active" : "warning";

  return (
    <div className="rounded-lg border border-border/60">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="flex w-full items-center gap-2 px-4 py-2.5 text-sm font-medium text-foreground"
      >
        <Network className="h-4 w-4 text-muted-foreground" strokeWidth={1.5} />
        Debug routing (resolver host)
        <span className="ml-auto text-xs text-muted-foreground">{open ? "Nascondi" : "Apri"}</span>
      </button>
      {open && (
        <div className="space-y-3 border-t border-border/60 p-4">
          <p className="text-xs text-muted-foreground">
            Mostra come il resolver classifica un host (default / platform / tenant / sospeso /
            sconosciuto) e lo schema risolto.
          </p>
          <form
            className="flex gap-2"
            onSubmit={(e) => {
              e.preventDefault();
              if (host.trim()) probe.mutate(host.trim());
            }}
          >
            <Input
              value={host}
              onChange={(e) => setHost(e.target.value)}
              placeholder="acme.tray.localhost:5173"
              aria-label="Host da risolvere"
            />
            <Button type="submit" variant="outline" disabled={probe.isPending || !host.trim()}>
              {probe.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : "Risolvi"}
            </Button>
          </form>
          {probe.isError && (
            <p className="text-sm text-danger">
              {probe.error instanceof PlatformApiError ? probe.error.message : "Errore di rete."}
            </p>
          )}
          {result && (
            <dl className="grid grid-cols-[8rem_1fr] gap-y-1.5 text-sm">
              <dt className="text-muted-foreground">Classificazione</dt>
              <dd>
                <Badge tone={toneFor(result.classification)}>{result.classification}</Badge>
              </dd>
              <dt className="text-muted-foreground">Slug</dt>
              <dd className="font-mono text-xs">{result.slug ?? "—"}</dd>
              <dt className="text-muted-foreground">Schema</dt>
              <dd className="font-mono text-xs">{result.schema_name ?? "—"}</dd>
              <dt className="text-muted-foreground">Base host</dt>
              <dd className="font-mono text-xs">{result.base_hostname}</dd>
            </dl>
          )}
        </div>
      )}
    </div>
  );
}

function StatCard({ label, value }: { label: string; value: number | undefined }) {
  return (
    <div className="rounded-lg border border-border/60 bg-surface px-4 py-3">
      <div className="text-xs uppercase tracking-wide text-muted-foreground">{label}</div>
      <div className="mt-1 text-2xl font-semibold tabular-nums">{value ?? "—"}</div>
    </div>
  );
}

/** Sentinel for the "create the organization inline" option — not a real id. */
const NEW_ORGANIZATION = "__new__";

function CreateTenantDialog({ onClose, onCreated }: { onClose: () => void; onCreated: () => void }) {
  const [name, setName] = React.useState("");
  const [slug, setSlug] = React.useState("");
  const [touched, setTouched] = React.useState(false);
  const [plan, setPlan] = React.useState<TenantPlan>("single");
  // The VIP/enterprise flow: cell + billing plan chosen AT creation. "" = the
  // default cell / no plan (UNLIMITED). A later cell move is a data migration.
  const [cellId, setCellId] = React.useState("");
  const [planKey, setPlanKey] = React.useState("");
  // The COMMERCIAL organization (ADR-0074 "modello Organizzazione"). "" = unattached; "__new__" pairs
  // with `organizationName` so the superadmin can onboard a brand-new company inline instead of
  // creating the anagrafica first and coming back.
  const [organizationId, setOrganizationId] = React.useState("");
  const [organizationName, setOrganizationName] = React.useState("");
  const effectiveSlug = touched ? slug : slugify(name);
  const validation = slugError(effectiveSlug);

  const cellsQ = useQuery({
    queryKey: ["platform", "cells"],
    queryFn: () => platformApi.listCells(),
  });
  const plansQ = useQuery({
    queryKey: ["platform", "plans"],
    queryFn: () => platformApi.listPlans(),
  });
  const organizationsQ = useQuery({
    queryKey: ["platform", "organizations", { search: "" }],
    queryFn: () => platformApi.listOrganizations({ status: "active" }),
  });
  const cells = cellsQ.data ?? [];
  const activeCells = cells.filter((c) => c.status === "active");
  const defaultCell = cells.find((c) => c.is_default) ?? null;

  const create = useMutation({
    mutationFn: () =>
      platformApi.createTenant({
        slug: effectiveSlug,
        name: name.trim() || undefined,
        plan,
        cell_id: cellId || null,
        plan_key: planKey || null,
        // An explicit id WINS server-side; the name path creates-or-reuses by name.
        organization_id: organizationId && organizationId !== NEW_ORGANIZATION ? organizationId : null,
        organization_name:
          organizationId === NEW_ORGANIZATION && organizationName.trim() ? organizationName.trim() : null,
      }),
    onSuccess: () => {
      onCreated();
      onClose();
    },
  });

  const serverError = create.isError
    ? create.error instanceof PlatformApiError
      ? create.error.message
      : "Creazione non riuscita"
    : null;

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Crea tenant</DialogTitle>
        </DialogHeader>
        <DialogBody className="space-y-4">
          <div className="space-y-1.5">
            <Label htmlFor="pt-name">Nome</Label>
            <Input
              id="pt-name"
              autoFocus
              value={name}
              placeholder="Acme Inc"
              onChange={(e) => setName(e.target.value)}
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="pt-slug">Slug</Label>
            <Input
              id="pt-slug"
              value={effectiveSlug}
              placeholder="acme"
              onChange={(e) => {
                setTouched(true);
                setSlug(slugify(e.target.value));
              }}
            />
            <p className="text-xs text-muted-foreground">
              Schema: <span className="font-mono">t_{effectiveSlug || "…"}</span>
            </p>
            {touched && validation && <p className="text-xs text-danger">{validation}</p>}
          </div>
          <div className="space-y-1.5">
            <Label>Piano</Label>
            <div className="flex gap-2">
              {(["single", "multi"] as const).map((p) => (
                <button
                  key={p}
                  type="button"
                  onClick={() => setPlan(p)}
                  className={
                    "flex-1 rounded-md border px-3 py-2 text-sm transition-colors " +
                    (plan === p
                      ? "border-accent bg-accent/10 font-medium text-foreground"
                      : "border-border/60 text-muted-foreground hover:bg-muted")
                  }
                >
                  {p === "single" ? "Single" : "Multi"}
                </button>
              ))}
            </div>
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="pt-organization">Organizzazione</Label>
            <select
              id="pt-organization"
              value={organizationId}
              onChange={(e) => setOrganizationId(e.target.value)}
            >
              <option value="">Nessuno</option>
              {(organizationsQ.data ?? []).map((c) => (
                <option key={c.id} value={c.id}>
                  {c.name}
                </option>
              ))}
              <option value={NEW_ORGANIZATION}>+ Nuovo organizzazione…</option>
            </select>
            {organizationId === NEW_ORGANIZATION && (
              <Input
                value={organizationName}
                placeholder="Nome del organizzazione"
                aria-label="Nome del nuovo organizzazione"
                onChange={(e) => setOrganizationName(e.target.value)}
              />
            )}
            <p className="text-xs text-muted-foreground">
              L'azienda a cui appartiene questo workspace. Un organizzazione può averne più d'uno, e
              può avere anche installazioni on-prem.
            </p>
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="pt-cell">Cella</Label>
            <select id="pt-cell" value={cellId} onChange={(e) => setCellId(e.target.value)}>
              <option value="">
                {defaultCell ? `Default (${defaultCell.name})` : "Automatica (default)"}
              </option>
              {activeCells.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.name}
                  {c.region ? ` — ${c.region}` : ""}
                </option>
              ))}
            </select>
            <p className="text-xs text-muted-foreground">
              La cella si sceglie ADESSO: spostare un tenant dopo è una migrazione. Per un
              tenant VIP provisiona prima la cella dedicata, poi selezionala qui.
            </p>
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="pt-plan-key">Piano di fatturazione (opzionale)</Label>
            <select id="pt-plan-key" value={planKey} onChange={(e) => setPlanKey(e.target.value)}>
              <option value="">Nessuno (illimitato)</option>
              {(plansQ.data ?? [])
                .filter((p) => p.active)
                .map((p) => (
                  <option key={p.key} value={p.key}>
                    {p.name} ({p.key})
                  </option>
                ))}
            </select>
            <p className="text-xs text-muted-foreground">
              Assegna subito un abbonamento (comp, senza Stripe) — es. il piano enterprise di
              un tenant VIP. Vuoto = nessun piano, entitlement illimitati.
            </p>
          </div>
          {serverError && <p className="text-sm text-danger">{serverError}</p>}
        </DialogBody>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose} disabled={create.isPending}>
            Annulla
          </Button>
          <Button
            onClick={() => create.mutate()}
            disabled={!!validation || create.isPending}
          >
            {create.isPending && <Loader2 className="h-4 w-4 animate-spin" />}
            Crea tenant
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function DeleteTenantDialog({
  tenant,
  pending,
  error,
  onConfirm,
  onClose,
}: {
  tenant: Tenant;
  pending: boolean;
  error: string | null;
  onConfirm: () => void;
  onClose: () => void;
}) {
  const [typed, setTyped] = React.useState("");
  const armed = typed.trim() === tenant.slug;

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Elimina tenant</DialogTitle>
        </DialogHeader>
        <DialogBody className="space-y-4">
          <div className="flex items-start gap-2 rounded-md border border-danger/30 bg-danger/5 p-3 text-sm">
            <TriangleAlert className="mt-0.5 h-4 w-4 shrink-0 text-danger" strokeWidth={1.5} />
            <p className="text-muted-foreground">
              Questa operazione elimina lo schema{" "}
              <span className="font-mono text-foreground">{tenant.schema_name}</span> e{" "}
              <strong className="text-foreground">tutti i suoi dati</strong>. È irreversibile.
            </p>
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="pt-del">
              Digita <span className="font-mono text-foreground">{tenant.slug}</span> per confermare
            </Label>
            <Input
              id="pt-del"
              autoFocus
              value={typed}
              onChange={(e) => setTyped(e.target.value)}
              placeholder={tenant.slug}
            />
          </div>
          {error && <p className="text-sm text-danger">{error}</p>}
        </DialogBody>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose} disabled={pending}>
            Annulla
          </Button>
          <Button variant="danger" onClick={onConfirm} disabled={!armed || pending}>
            {pending && <Loader2 className="h-4 w-4 animate-spin" />}
            Elimina definitivamente
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
